<template>
    <!-- A result page for a kit that has no results yet: where the kit is up to, and a link to
         the full picture on the Recent Ancestry overview. Or the same for an upload. -->
    <div class="kit-notice-wrap">
        <section class="kit-notice" :class="{ 'kit-notice--failed': failed }">
            <h2 class="kit-notice__title">
                {{ failed ? (failedTitle ?? `${product} isn't available`) : (title ?? `${product} will be ready when your kit finishes`) }}
            </h2>
            <template v-if="uploadPending">
                <p class="kit-notice__text">{{ uploadText }}</p>
                <router-link v-if="rawStatus === 'transfer_qc_failed'"
                    :to="{ name: 'reupload-dna', params: { barcode } }" class="ldna-btn kit-notice__link">
                    Reupload your DNA
                </router-link>
                <router-link v-else-if="failed" :to="{ name: 'recent-overview' }" class="ldna-btn kit-notice__link">
                    See what happens next
                </router-link>
            </template>
            <p v-else-if="failure" class="kit-notice__text">{{ failure.title }}.</p>
            <p v-else-if="currentStep" class="kit-notice__text">
                Your kit is at <strong>{{ currentStep.name }}</strong><template v-if="currentStep.duration">,
                which usually takes {{ currentStep.duration }}</template>. Results usually take 6–8 weeks from
                when we receive your sample.
            </p>
            <router-link v-if="!uploadPending" :to="{ name: 'recent-overview' }" class="ldna-btn kit-notice__link">
                {{ failure ? "See what happens next" : "See your kit's progress" }}
            </router-link>
            <slot />
        </section>
    </div>
</template>

<script setup>
import { computed, defineProps } from "vue";
import { useAncestryKit } from "@/composables/useAncestryKit";
import { isFailedKitStatus } from "@/services/kitService";

defineProps({
    // What the page shows once there are results, e.g. "Motherline"
    product: { type: String, required: true },
    // In place of "<product> will be ready when your kit finishes", while the kit is in progress
    title: { type: String, default: null },
    // In place of "<product> isn't available", for a kit that won't give results
    failedTitle: { type: String, default: null },
});

const { barcode, rawStatus, failure, currentStep, uploadPending } = useAncestryKit();

// An upload that failed, as its timeline says. Any other status reads as being tested, as the
// profile pill shows it.
const uploadFailed = computed(() => uploadPending.value && isFailedKitStatus(barcode.value, rawStatus.value));

const failed = computed(() => !!failure.value || uploadFailed.value);

// An upload is tested in a few hours, rather than a kit's weeks in the lab
const uploadText = computed(() => {
    if (rawStatus.value === "transfer_qc_failed") return "There was a problem reading your DNA file, so it needs uploading again.";
    if (uploadFailed.value) return "We couldn't test your uploaded DNA file.";
    return "Your upload is being tested, which usually takes a few hours.";
});
</script>

<style lang="scss" scoped>
.kit-notice-wrap {
    width: 100%;
    padding: 40px 16px;
}

.kit-notice {
    max-width: 640px;
    margin: 0 auto;
    padding: 28px;
    border-radius: 20px;
    background: #fff;
    border-top: 4px solid #154096;
    box-shadow: 0 4px 16px rgba(26, 31, 54, 0.06);
    text-align: left;

    &--failed { border-top-color: #d63c3c; }
}

.kit-notice__title {
    margin: 0 0 10px;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.3;
    color: #1A1F36;
}

.kit-notice__text {
    margin: 0 0 20px;
    font-size: 15px;
    line-height: 1.6;
    color: #2E3A59;
}

// Sized to its words and lined up with the text, rather than the full-width centred .ldna-btn
.kit-notice__link.kit-notice__link.kit-notice__link {
    display: inline-flex;
    width: auto;
    margin-left: 0;
    padding: 12px 24px;
}

@media (max-width: 575px) {
    .kit-notice-wrap { padding: 20px 0; }
    .kit-notice { padding: 20px 16px; border-radius: 16px; }
    .kit-notice__title { font-size: 18px; }
    .kit-notice__link.kit-notice__link.kit-notice__link { display: flex; width: 100%; }
}
</style>
